import React from 'react';

/* Number formatting is part of the design, not a utility-function detail.
   Tabular sans, slashed zero, always signed for P&L and percentages, U+2212
   minus (which aligns with digits), and an em dash — never 0, never blank —
   when a value is absent. */

const STEP = {
  display: ['var(--num-display-size)', 'var(--num-display-line)', 'var(--num-display-weight)'],
  xl: ['var(--num-xl-size)', 'var(--num-xl-line)', 'var(--num-xl-weight)'],
  lg: ['var(--num-lg-size)', 'var(--num-lg-line)', 'var(--num-lg-weight)'],
  md: ['var(--num-md-size)', 'var(--num-md-line)', 'var(--num-md-weight)'],
  sm: ['var(--num-sm-size)', 'var(--num-sm-line)', 'var(--num-sm-weight)'],
  xs: ['var(--num-xs-size)', 'var(--num-xs-line)', 'var(--num-xs-weight)'],
};

export function formatNum(value, { kind = 'plain', currency = '€', dp } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  const places = dp != null ? dp : (kind === 'percent' ? 1 : kind === 'ratio' ? 2 : 2);
  const abs = Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: places, maximumFractionDigits: places });
  const sign = value < 0 ? '\u2212' : '+';
  if (kind === 'money') return (value < 0 ? '\u2212' : '') + currency + abs;
  if (kind === 'pnl') return sign + currency + abs;
  if (kind === 'percent') return sign + abs + '%';
  /* A ratio takes the minus but never the plus: it is not a signed P&L, so a leading '+'
     would read as a gain. Returning the bare absolute \u2014 as this did until 2026-08-04 \u2014
     renders a Sharpe of \u22120.34 as 0.34, which is a losing strategy wearing a winner's
     number on the screen that decides which one gets real money.

     \u26a0 This implemented `type-number-formatting`'s own "2dp, unsigned" faithfully. The
     specimen meant *no leading plus*, which is what the two rows above it contrast with \u2014
     but 0.94 was its only example, so it never said what a negative should do, and the
     dangerous reading is the one that got written. The card now shows \u22120.34. */
  if (kind === 'ratio') return (value < 0 ? '\u2212' : '') + abs;
  return (value < 0 ? '\u2212' : '') + abs;
}

export function Num({ value, kind = 'plain', step = 'md', dp, currency = '€', stale = false, arrow = false, colour = true, style, children }) {
  const [size, line, weight] = STEP[step] || STEP.md;
  const signed = kind === 'pnl' || kind === 'percent';
  const numeric = typeof value === 'number';
  const absent = value === null || value === undefined || value === '';
  const tone = stale ? 'var(--text-disabled)'
    : (colour && signed && numeric ? (value >= 0 ? 'var(--profit)' : 'var(--loss)') : 'var(--text-primary)');

  return (
    <span className="jmt-num" style={{
      fontFamily: 'var(--font-ui)', fontVariantNumeric: 'tabular-nums slashed-zero',
      fontSize: size, lineHeight: line, fontWeight: weight,
      color: absent ? 'var(--text-tertiary)' : tone, whiteSpace: 'nowrap', ...style,
    }}>
      {children != null ? children : formatNum(value, { kind, currency, dp })}
      {arrow && numeric && !absent ? (
        <span aria-hidden="true" style={{ fontSize: '.85em', marginLeft: 4 }}>{value >= 0 ? '▲' : '▼'}</span>
      ) : null}
    </span>
  );
}
